import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from '../../services/api';
import useEventoStore from '../../stores/useEventoStore';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // escuchamos 401 globales del api
  useEffect(() => {
    const handle401 = () => {
      localStorage.removeItem("token");
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = "/login";
    };
    window.addEventListener('auth:unauthorized', handle401);
    return () => window.removeEventListener('auth:unauthorized', handle401);
  }, []);

  // verificamos al iniciar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          // chequeamos la expiracion del token
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (Date.now() < payload.exp * 1000) {
            setIsAuthenticated(true);
            // traemos el user
            const userData = await api.auth.getCurrentUser();
            if (userData.status === "success") {
              setUser(userData.data.usuario);
            }
          } else {
            localStorage.removeItem("token");
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (error) {
          localStorage.removeItem("token");
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, otp, deviceId, trusted) => {
    try {
      const result = await api.auth.Verify({ email, otp, deviceId, trusted });

      if (result.status === "success" && result.data.token) {
        localStorage.setItem("token", result.data.token);
        if (trusted) {
          localStorage.setItem(`trusted:${email}`, deviceId);
        }

        setIsAuthenticated(true);

        try {
          const userData = await api.auth.getCurrentUser();
          if (userData.status === "success") {
            setUser(userData.data.usuario);
          }
        } catch (userError) {
          console.error("Error al obtener datos del usuario:", userError);
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error durante el login:", error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    useEventoStore.getState().reset();
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = "/";
  };

  const loginWithRedirect = (returnTo = "/") => {
    let redirectTo = "/";
    if (typeof returnTo === "string") {
      redirectTo = returnTo;
    } else if (returnTo && typeof returnTo === "object" && returnTo.appState && returnTo.appState.returnTo) {
      redirectTo = returnTo.appState.returnTo;
    }
    window.location.href = `/login?returnTo=${encodeURIComponent(redirectTo)}`;
  };

  const logoutWithRedirect = (returnTo = "/") => {
    localStorage.removeItem("token");
    useEventoStore.getState().reset();
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = returnTo;
  };

  const getToken = () => {
    return localStorage.getItem("token");
  };

  const refreshUser = async () => {
    try {
      const userData = await api.auth.getCurrentUser();
      if (userData.status === "success") {
        setUser(userData.data.usuario);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error al refrescar usuario:", error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        loginWithRedirect,
        logoutWithRedirect,
        getToken,
        refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);